import React, { useContext, useMemo, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import { useNavigate } from "react-router-dom";

import * as Md from "react-icons/md";
import * as Io5 from "react-icons/io5";
import { set } from "date-fns";
import { AppContext } from "../context/AppContext";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { PulseLoader } from "react-spinners";
import Utils from "../utils/Utils";
import EventsSelecter from "../components/custom/events/EventsSelecter";

function AddEvents() {
  const { createNewEvents, eventsLoading, getEvents, promotion, menuItems } =
    useContext(AppContext);
  const navigate = useNavigate();
  const nextDayFromToday = new Date(
    new Date().setDate(new Date().getDate() + 1)
  )
    .toISOString()
    .split("T")[0];

  const [eventDetails, setEventDetails] = useState({
    event_title: "",
    event_start: new Date().toISOString().split("T")[0],
    event_end: nextDayFromToday,
    event_description: "",
    event_type: "default",
    menu_type: "default",
    menu_id: 0,
    maxAttendees: 0,
  });

  const [showPromotion, setShowPromotion] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);

  const [isError, setIsError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setEventDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const emptyFieldValidation = () => {
    const requiredFields = [
      "event_title",
      "event_start",
      "event_end",
      "event_type",
      "menu_type",
      "maxAttendees",
    ];

    const isAnyEmpty = requiredFields.some((field) => !eventDetails[field]);

    if (
      isAnyEmpty ||
      eventDetails.event_start > eventDetails.event_end ||
      eventDetails.maxAttendees <= 0
    ) {
      setIsError(true);
      return false;
    }

    setIsError(false);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = emptyFieldValidation();

    if (!isValid) return;
    const { menu_id, maxAttendees } = eventDetails;
    const payload = {
      ...eventDetails,
      menu_id: parseInt(menu_id) ?? 0,
      maxAttendees: parseInt(maxAttendees),
    };

    await createNewEvents(payload, {
      onSuccess: async (res) => {
        toast.success("Event created successfully");
        await getEvents();
        setTimeout(() => {
          navigate("/events");
        }, 1500);
      },
      onFailed: (err) => {
        toast.error("Failed to create event");
        console.log("Error in creating event");
      },
    });
  };

  const findPromotionDetails = (id) => {
    if (!promotion || promotion.length === 0 || !id || id === "0") return;

    const data = promotion.find((promo) => promo.offerId == id);
    return data;
  };

  const selected = useMemo(
    () => findPromotionDetails(selectedPromotion),
    [selectedPromotion, findPromotionDetails]
  );

  const parseDMY = (s) => {
    if (!s) return null;
    const [dd, mm, rest] = s.split("-");
    const [yyyy, time = "00:00:00"] = rest.split(" ");
    const d = new Date(`${yyyy}-${mm}-${dd}T${time}`);
    return isNaN(d) ? null : d;
  };

  return (
    <MasterLayout>
      <button type="button" className="back_btn" onClick={() => navigate(-1)}>
        <Md.MdKeyboardArrowLeft /> Back
      </button>
      <div className="card h-100 p-3 radius-12 event_card position-relative">
        <form onSubmit={handleSubmit}>
          {/* Event Name */}
          <div className="mb-3">
            <label htmlFor="eventName" className="form-label">
              Event Name
            </label>
            <input
              type="text"
              className="form-control"
              id="eventName"
              placeholder="Enter event name"
              name="event_title"
              onChange={handleChange}
              value={eventDetails.event_title}
            />
            {isError && !eventDetails.event_title && (
              <small className="text-danger">Event name is required</small>
            )}
          </div>

          {/* Dates */}
          <div className="mb-3">
            <div className="row">
              <div className="col-lg-6 col-md-6 col-sm-12">
                <label htmlFor="eventStart" className="form-label">
                  Event Start Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="eventStart"
                  name="event_start"
                  onChange={(e) => {
                    const selectedDate = new Date(e.target.value);
                    const nextDate = new Date(selectedDate);
                    nextDate.setDate(selectedDate.getDate() + 1);

                    setEventDetails({
                      ...eventDetails,
                      event_start: e.target.value,
                      event_end: nextDate.toISOString().split("T")[0],
                    });
                  }}
                  value={eventDetails.event_start}
                />
                {isError && !eventDetails.event_start && (
                  <small className="text-danger">Start date is required</small>
                )}
                {eventDetails.event_start > eventDetails.event_end && (
                  <small className="text-danger">
                    Event Start Date cannot be after End Date!
                  </small>
                )}
              </div>

              <div className="col-lg-6 col-md-6 col-sm-12">
                <label htmlFor="eventEnd" className="form-label">
                  Event End Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="eventEnd"
                  name="event_end"
                  onChange={handleChange}
                  value={eventDetails.event_end}
                />
                {isError && !eventDetails.event_end && (
                  <small className="text-danger">End date is required</small>
                )}

                {eventDetails.event_start > eventDetails.event_end && (
                  <small className="text-danger">
                    Event End Date cannot be before Start Date!
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mb-3">
            <label htmlFor="eventDescription" className="form-label">
              Event Description
            </label>
            <textarea
              className="form-control"
              id="eventDescription"
              rows="4"
              placeholder="Enter event description"
              name="event_description"
              onChange={handleChange}
              value={eventDetails.event_description}
            ></textarea>
          </div>

          {/* Event Type & Menu Type */}
          <div className="mb-3">
            <div className="row">
              <div className="col-lg-6 col-md-6 col-sm-12">
                <label htmlFor="event_type" className="form-label">
                  Event Type
                </label>

                <EventsSelecter
                  handleChange={handleChange}
                  eventDetails={eventDetails}
                />

                {isError && !eventDetails.event_type && (
                  <small className="text-danger">Select event type</small>
                )}
              </div>

              <div className="col-lg-6 col-md-6 col-sm-12">
                <label htmlFor="menu_type" className="form-label">
                  Menu Type
                </label>
                <select
                  name="menu_type"
                  className="form-select"
                  onChange={handleChange}
                  value={eventDetails.menu_type}
                >
                  <option value="default">Default</option>
                  <option value="special">Special</option>
                </select>
                {isError && eventDetails.menu_type.length === 0 && (
                  <small className="text-danger">Select menu type</small>
                )}
              </div>
            </div>
          </div>

          {/* Menu & Max Attendees */}
          <div className="mb-3">
            <div className="row">
              <div className="col-lg-6 col-md-6 col-sm-12">
                <label htmlFor="menu_id" className="form-label">
                  Menu
                </label>
                <select
                  name="menu_id"
                  className="form-select"
                  onChange={handleChange}
                  value={eventDetails.menu_id}
                >
                  <option value="0">Default</option>
                  {menuItems &&
                    menuItems
                      .filter((x) => x.active == "Yes")
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.title}
                        </option>
                      ))}
                </select>
                {isError && !eventDetails.menu_id && (
                  <small className="text-danger">Select a menu</small>
                )}
              </div>

              <div className="col-lg-6 col-md-6 col-sm-12">
                <label htmlFor="maxAttendees" className="form-label">
                  Maximum Attendees
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0"
                  id="maxAttendees"
                  name="maxAttendees"
                  onChange={handleChange}
                  value={eventDetails.maxAttendees}
                />
                {isError && !eventDetails.maxAttendees && (
                  <small className="text-danger">
                    Maximum attendees is required
                  </small>
                )}
              </div>
            </div>
          </div>

          <hr />
          <br />
          {/* Promotion Section Toggle */}
          <div className="text-center mb-3">
            {!showPromotion ? (
              <a
                role="button"
                className="promotion_option_btn"
                onClick={() => setShowPromotion(!showPromotion)}
              >
                Do you want to add a promotion for this event?
              </a>
            ) : (
              ""
            )}
          </div>
          {showPromotion && (
            <button
              type="button"
              className="mb-4"
              onClick={() => {
                setShowPromotion(false);
                setSelectedPromotion(0);
              }}
            >
              <Io5.IoClose />
            </button>
          )}
          {/* Slide Down Promotion Details */}
          <AnimatePresence>
            {showPromotion && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="card p-3 mb-3 bg-light border rounded-3 overflow-hidden">
                  <label htmlFor="promo_title">Promotion List</label>
                  <select
                    name="promo_title"
                    id=""
                    className="form-select"
                    onChange={(e) => setSelectedPromotion(e.target.value)}
                  >
                    <option value="" disabled selected>
                      Select a promotion
                    </option>
                    {promotion &&
                      promotion
                        .filter((x) => x.active === true)
                        .map((promo) => (
                          <option key={promo.id} value={promo.offerId}>
                            {promo.title}
                          </option>
                        ))}
                  </select>
                  {selectedPromotion && selectedPromotion != 0 && (
                    <>
                      <label htmlFor="" className="form-label">
                        Promotion Details
                      </label>
                      <div className="p-3 border bg-white rounded-3">
                        {selected ? (
                          <div className="mb-4">
                            <div className="d-flex gap-3">
                              {selected.wFile && (
                                <img
                                  src={selected.wFile}
                                  alt={selected.title}
                                  style={{
                                    width: 250,
                                    height: 80,
                                    objectFit: "contain",
                                    borderRadius: 8,
                                  }}
                                />
                              )}
                              {selected.file && (
                                <img
                                  src={selected.file}
                                  alt={selected.title}
                                  style={{
                                    width: 250,
                                    height: 80,
                                    objectFit: "contain",
                                    borderRadius: 8,
                                  }}
                                />
                              )}
                            </div>
                            <br />
                            <div>
                              <h6 className="mb-1">{selected.title}</h6>
                              <div className="text-muted mb-2">
                                {selected.description || "—"}
                              </div>

                              <div>
                                <strong>Offer ID:</strong> {selected.offerId}
                              </div>
                              <div>
                                <strong>Offer Code:</strong>{" "}
                                {selected.offerCode || "—"}
                              </div>
                              <div>
                                <strong>Channels:</strong>{" "}
                                {[
                                  selected.web && "Web",
                                  selected.mobile && "Mobile",
                                ]
                                  .filter(Boolean)
                                  .join(" & ") || "—"}
                              </div>
                              <div>
                                <strong>Positions:</strong>{" "}
                                {[selected.wPosition, selected.mPosition]
                                  .filter(Boolean)
                                  .join(" / ") || "—"}
                              </div>
                              <div>
                                <strong>Promotion For:</strong>{" "}
                                {selected.promotionFor || "—"}
                              </div>
                              <div>
                                <strong>Payment Type:</strong>{" "}
                                {selected.paymentType || "—"}
                              </div>
                              <div>
                                <strong>Price:</strong> {selected.price ?? "—"}
                                {selected.discount_price
                                  ? ` (discount: ${selected.discount_price})`
                                  : ""}
                              </div>
                              <div>
                                <strong>Validity:</strong>{" "}
                                {Utils.formatDate(selected.startDate)} →{" "}
                                {selected?.endDate
                                  ? parseDMY(selected.endDate)
                                    ? Utils.formatDate(
                                        parseDMY(selected.endDate)
                                      )
                                    : "—"
                                  : "—"}
                              </div>
                              <div>
                                <strong>Usage Count:</strong>{" "}
                                {selected.usage_count ?? 0}
                              </div>
                              <div>
                                <strong>Delivery:</strong>{" "}
                                {[
                                  selected.doorDelivery && "Door Delivery",
                                  selected.takeAway && "Take Away",
                                ]
                                  .filter(Boolean)
                                  .join(" & ") || "—"}
                              </div>
                              <div>
                                <strong>Status:</strong>{" "}
                                {selected.active ? "Active" : "Inactive"}
                              </div>
                              {selected.redirectWebLink && (
                                <div>
                                  <strong>Redirect:</strong>{" "}
                                  <a
                                    href={selected.redirectWebLink}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {selected.redirectWebLink}
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <>No promotion selected.</>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <br />
          <button type="submit" className="submit_btn" disabled={eventsLoading}>
            {eventsLoading ? (
              <PulseLoader color="#fff" size={10} />
            ) : (
              "Create Event"
            )}
          </button>
        </form>
      </div>
    </MasterLayout>
  );
}

export default AddEvents;
